import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';
import { errorHandler } from '../../utils/errorHandler';
import { hasFeatureAccess } from '../../utils/featurePermission';
//
import { dispatch } from '../store';
import { updateError } from './user';

// ---------------------------------------------------------------------
const initialState = {
    tiktokAccount: null,
    tiktokAdvertisers: [],
    tiktokLeadGenForms: [],
    availableAdvertisers: [],
    isLoading: false,
    isConnecting: false,
};

const slice = createSlice({
    name: 'tiktokIntegration',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        startConnecting(state) {
            state.isConnecting = true;
        },
        stopConnecting(state) {
            state.isConnecting = false;
        },
        setTikTokAccount(state, action) {
            state.tiktokAccount = action.payload;
        },
        setTikTokAdvertisers(state, action) {
            state.tiktokAdvertisers = action.payload;
        },
        setTikTokLeadGenForms(state, action) {
            state.tiktokLeadGenForms = action.payload;
        },
        setAvailableAdvertisers(state, action) {
            state.availableAdvertisers = action.payload;
        },
    }
});

export const { reset, setTikTokAccount } = slice.actions;

export default slice.reducer;

// ----------------------------- Thunks --------------------------------

export function getTikTokAccount(practiceId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/`);
            if (response.status === 200) {
                const results = response.data?.results || response.data;
                const account = Array.isArray(results) && results.length > 0 ? results[0] : null;
                dispatch(slice.actions.setTikTokAccount(account));
            } else if (response.status !== 403) {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function tiktokConnectUrl(practiceId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/connect/`);
            if (response.status === 200) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: false, message: response.data.message || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        }
    };
}

export function tiktokCallback(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/callback/`, 'post', data);
            if (response.status === 200) {
                handleSuccess({ success: true, message: 'TikTok connected successfully' });
            } else {
                handleSuccess({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' });
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        }
    };
}

export function disconnectTikTok(practiceId, accountId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/disconnect/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'TikTok account disconnected successfully!' }));
                dispatch(slice.actions.setTikTokAccount(null));
                dispatch(slice.actions.setTikTokAdvertisers([]));
                dispatch(slice.actions.setTikTokLeadGenForms([]));
                dispatch(slice.actions.setAvailableAdvertisers([]));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false, message: JSON.stringify(response?.data) });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
        }
    };
}

export function getAvailableAdvertisers(practiceId, accountId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/available_advertisers/`);
            if (response.status === 200) {
                dispatch(slice.actions.setAvailableAdvertisers(response.data?.results || response.data || []));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function connectAdvertisers(practiceId, accountId, advertiserIds, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startConnecting());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/connect_advertisers/`, 'post', { advertiser_ids: advertiserIds });
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Advertisers connected successfully' }));
                if (handleSuccess) handleSuccess({ success: true });
                dispatch(getTikTokAdvertisers(practiceId, accountId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false });
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopConnecting());
        }
    };
}

export function syncAdvertisers(practiceId, accountId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/sync_advertisers/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Advertisers synced successfully' }));
                dispatch(getTikTokAdvertisers(practiceId, accountId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function syncAllForms(practiceId, accountId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/sync_all_forms/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Forms sync scheduled for all advertisers' }));
                dispatch(getTikTokAdvertisers(practiceId, accountId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function getTikTokAdvertisers(practiceId, accountId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/`);
            if (response.status === 200) {
                dispatch(slice.actions.setTikTokAdvertisers(response.data?.results || response.data || []));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function syncForms(practiceId, accountId, advertiserId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/${advertiserId}/sync_forms/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Forms synced successfully' }));
                dispatch(getTikTokForms(practiceId, accountId, advertiserId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function getTikTokForms(practiceId, accountId, advertiserId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/${advertiserId}/tiktok-lead-gen-forms/`);
            if (response.status === 200) {
                dispatch(slice.actions.setTikTokLeadGenForms(response.data?.results || response.data || []));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function syncLeads(practiceId, accountId, advertiserId, formId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/${advertiserId}/tiktok-lead-gen-forms/${formId}/sync_leads/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Leads synced successfully' }));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function toggleFormActive(practiceId, accountId, advertiserId, formId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/${advertiserId}/tiktok-lead-gen-forms/${formId}/toggle_active/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Form status updated' }));
                dispatch(getTikTokForms(practiceId, accountId, advertiserId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        }
    };
}

export function assignFormPractice(practiceId, accountId, advertiserId, formId, targetPracticeId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tiktok-accounts/${accountId}/tiktok-advertisers/${advertiserId}/tiktok-lead-gen-forms/${formId}/assign_practice/`,
                'post',
                { practice_id: targetPracticeId }
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: targetPracticeId ? 'Practice assigned to form' : 'Practice assignment removed' }));
                dispatch(getTikTokForms(practiceId, accountId, advertiserId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        }
    };
}
